import { useState } from "react";
import { toast } from "sonner";
import { Notice } from "./kit";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api, errMsg } from "../lib/api";

const EMPTY = { email: "", first_name: "", last_name: "" };

export function ContactDialog({ open, onClose, audId, onDone }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/app/audiences/${audId}/contacts`, f); toast.success("Contact saved"); setF(EMPTY); onDone(); onClose(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-white/10 bg-[#0F0E13] sm:max-w-md" data-testid="contact-dialog">
        <DialogHeader><DialogTitle>Add contact</DialogTitle><DialogDescription>Only add people who agreed to receive email from you.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {[["email", "Email", "email"], ["first_name", "First name", "text"], ["last_name", "Last name", "text"]].map(([k, label, type]) => (
            <div key={k} className="space-y-1.5"><Label>{label}</Label>
              <Input type={type} required={k === "email"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contact-${k}-input`} /></div>))}
          <DialogFooter><Button disabled={busy} data-testid="contact-save-button">Save contact</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ImportDialog({ open, onClose, audId, onDone }) {
  const [csv, setCsv] = useState("");
  const [fileName, setFileName] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const pick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_000_000) { toast.error("The file is larger than 1 MB. Split it into smaller files."); return; }
    setFileName(file.name);
    file.text().then(setCsv);
  };
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/app/audiences/${audId}/import`, { csv, consent }); setResult(data); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const close = () => { setCsv(""); setFileName(""); setConsent(false); setResult(null); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="border-white/10 bg-[#0F0E13] sm:max-w-lg" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Import contacts from CSV</DialogTitle>
          <DialogDescription>Header row with an <code>email</code> column. Optional: <code>first_name</code>, <code>last_name</code> and up to 20 more columns you can use as {"{{column_name}}"}. Up to 20,000 rows / 1 MB.</DialogDescription></DialogHeader>
        {result ? (
          <Notice tone="ok" title="Import finished" testId="import-result">
            {result.added} added · {result.updated} updated · {result.invalid} invalid
            {result.invalid_rows?.length > 0 && <div className="mt-1 text-xs">Invalid rows: {result.invalid_rows.map((r) => r.row).join(", ")}</div>}
          </Notice>
        ) : (
          <div className="space-y-4">
            <Input type="file" accept=".csv,text/csv" onChange={pick} data-testid="import-file-input" />
            {fileName && <p className="text-xs text-zinc-400" data-testid="import-file-name">{fileName} · {csv.split("\n").length - 1} rows</p>}
            <label className="flex items-start gap-2 text-sm text-zinc-300">
              <Checkbox checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="import-consent-checkbox" />
              I confirm everyone in this file agreed to receive email from us. Bought or scraped lists are not allowed.
            </label>
          </div>)}
        <DialogFooter>
          {result ? <Button onClick={close} data-testid="import-done-button">Done</Button>
            : <Button disabled={busy || !csv || !consent} onClick={submit} data-testid="import-submit-button">{busy ? "Importing…" : "Import"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
